import { Search, Expand, Shrink, RefreshCw } from 'lucide-react'
import { EDGE_COLORS, CONCEPT_CATEGORY_COLORS } from './mindmap-layout'

interface MindMapToolbarProps {
  searchFilter: string
  onSearchChange: (value: string) => void
  onExpandAll: () => void
  onCollapseAll: () => void
  onRefresh: () => void
  isExtracting: boolean
  stats: {
    groups: number
    documents: number
    concepts: number
    crossRefs: number
  }
}

export function MindMapToolbar({
  searchFilter,
  onSearchChange,
  onExpandAll,
  onCollapseAll,
  onRefresh,
  isExtracting,
  stats,
}: MindMapToolbarProps) {
  return (
    <div className="absolute top-3 left-3 z-10 flex flex-col gap-2">
      {/* Search */}
      <div className="flex items-center gap-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-1.5 shadow-sm">
        <Search className="w-3.5 h-3.5 text-gray-400" />
        <input
          type="text"
          value={searchFilter}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search papers, concepts..."
          className="text-xs bg-transparent border-none outline-none text-gray-700 dark:text-gray-200 placeholder-gray-400 w-44"
        />
      </div>

      {/* Controls */}
      <div className="flex items-center gap-1">
        <button
          onClick={onExpandAll}
          className="p-1.5 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shadow-sm"
          title="Expand all groups"
        >
          <Expand className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300" />
        </button>
        <button
          onClick={onCollapseAll}
          className="p-1.5 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shadow-sm"
          title="Collapse all"
        >
          <Shrink className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300" />
        </button>
        <button
          onClick={onRefresh}
          disabled={isExtracting}
          className="p-1.5 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shadow-sm disabled:opacity-50"
          title="Extract concepts for new documents"
        >
          <RefreshCw className={`w-3.5 h-3.5 text-gray-600 dark:text-gray-300 ${isExtracting ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Stats */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 shadow-sm">
        <div className="text-[10px] text-gray-500 dark:text-gray-400 space-y-0.5">
          <div>{stats.groups} groups, {stats.documents} papers</div>
          <div>{stats.concepts} concepts, {stats.crossRefs} cross-refs</div>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 shadow-sm max-h-64 overflow-y-auto">
        <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider">Edges</div>
        <div className="space-y-1">
          {Object.entries(EDGE_COLORS).map(([type, color]) => (
            <div key={type} className="flex items-center gap-1.5">
              <div className="w-4 h-0.5 rounded" style={{ backgroundColor: color }} />
              <span className="text-[10px] text-gray-600 dark:text-gray-400">{type}</span>
            </div>
          ))}
        </div>
        <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 mt-2 mb-1.5 uppercase tracking-wider">Concepts</div>
        <div className="space-y-1">
          {Object.entries(CONCEPT_CATEGORY_COLORS).map(([cat, color]) => (
            <div key={cat} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
              <span className="text-[10px] text-gray-600 dark:text-gray-400">{cat}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
